
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import "./EventParticipants.scss";
import { Dialog, Stack, Typography } from "@mui/material";
import { type Association, type Group, type Opportunity, type OpportunityParticipant } from "@entities";
import { UserCardPopup } from "../popups/user-card/UserCardPopup";
import clsx from "clsx";
import ParticipantsTable from "../../pages/tables/opprtunity-participants";
import OptionalParticipantsTable from "../../pages/tables/optional-opportunity-participant";
import OptionalVolunteerGroupsTable from "../../pages/tables/optional-group-table";
import ParticipantsGroupTable from "../../pages/tables/participants-group-table";
import {
  useQuery,
  type QueryObserverResult,
  type RefetchOptions,
} from "@tanstack/react-query";
import OpportunityRequestsTable from "../../pages/tables/opprtunity-interested";
import { allowsGroupVolunteer } from "@shared/helpers/opportunity-volunteer-mode";
import {
  groupOpportunityRepo,
  opportunityParticipantRepo,
} from "@repositories";
import { EVENT_PARTICIPANT_TAB_COUNTS_QUERY_KEY } from "../../queries/invalidate-opportunities-table";

interface EventParticipantsProps {
  eventDetails: Opportunity | undefined;
  isAdd: boolean;
  refetchEvent: (options?: RefetchOptions) => Promise<QueryObserverResult<Opportunity | null | undefined, Error>>;
  tourAnchors?: boolean;
  /** Tour: open the groups tab and its real table. */
  tourShowGroups?: boolean;
  previewGroups?: Group[];
  previewAssociations?: Association[];
  previewParticipants?: OpportunityParticipant[];
}

type ParticipantView = "groups" | "individuals" | "pending";

function resolveActiveView(
  participantView: ParticipantView,
  isGroupOpportunity: boolean,
): ParticipantView {
  if (!isGroupOpportunity && participantView === "groups") {
    return "individuals";
  }
  return participantView;
}

function pickViewForCounts(
  isGroupOpportunity: boolean,
  counts: { approved: number; pending: number; groupLinks: number },
): ParticipantView {
  if (counts.groupLinks > 0 && isGroupOpportunity) return "groups";
  if (counts.approved > 0) return "individuals";
  if (counts.pending > 0) return "pending";
  return isGroupOpportunity ? "groups" : "individuals";
}

function formatTabCount(label: string, count?: number) {
  if (count == null) return label;
  return `${label} (${count})`;
}

export const EventParticipants: FC<EventParticipantsProps> = ({
  eventDetails,
  isAdd,
  refetchEvent,
  tourAnchors = false,
  tourShowGroups = false,
  previewGroups,
}) => {
  const [iframeUrl, setIframeUrl] = useState<string>("");
  const [currentUserRowData, setCurrentUserRowData] = useState<Association>();
  const opportunityId = eventDetails?.id ?? "";
  const isGroupOpportunity =
    allowsGroupVolunteer(eventDetails?.volunteerMode) || tourShowGroups;
  const [participantView, setParticipantView] = useState<ParticipantView>(() =>
    isGroupOpportunity ? "groups" : "individuals",
  );
  const autoPickedForId = useRef("");

  const { data: tabCounts } = useQuery({
    queryKey: [EVENT_PARTICIPANT_TAB_COUNTS_QUERY_KEY, opportunityId],
    enabled: !!opportunityId && !isAdd,
    queryFn: async () => {
      const [approved, pending, groupLinks] = await Promise.all([
        opportunityParticipantRepo.count({
          opportunity: { $id: opportunityId },
          status: "APPROVED",
        }),
        opportunityParticipantRepo.count({
          opportunity: { $id: opportunityId },
          status: "PENDING",
        }),
        groupOpportunityRepo.count({
          opportunityId,
        }),
      ]);
      return { approved, pending, groupLinks };
    },
  });

  const activeView = useMemo(
    () => resolveActiveView(participantView, isGroupOpportunity),
    [participantView, isGroupOpportunity],
  );

  useEffect(() => {
    if (tourShowGroups) {
      setParticipantView("groups");
      return;
    }
    autoPickedForId.current = "";
    if (isAdd) {
      setParticipantView((v) =>
        !isGroupOpportunity && v === "groups" ? "individuals" : v,
      );
      return;
    }
    setParticipantView(isGroupOpportunity ? "groups" : "individuals");
  }, [
    opportunityId,
    isAdd,
    isGroupOpportunity,
    eventDetails?.volunteerMode,
    tourShowGroups,
  ]);

  useEffect(() => {
    if (tourShowGroups || isAdd || !tabCounts || !opportunityId) return;
    if (autoPickedForId.current === opportunityId) return;
    autoPickedForId.current = opportunityId;
    setParticipantView(pickViewForCounts(isGroupOpportunity, tabCounts));
  }, [isAdd, tabCounts, opportunityId, isGroupOpportunity, tourShowGroups]);

  return (
    <div
      className={clsx("event-participants-container", isAdd && "add")}
      data-tour={tourAnchors ? "cc-participants" : undefined}
    >
      <p className="par">{isAdd ? "הוספת משתתפים" : "משתתפים"}</p>
        <Stack flexDirection="row" gap="1rem" marginTop="1rem" flexWrap="wrap">
          {isGroupOpportunity && (
            <button
              type="button"
              className={clsx(
                "add-type-button",
                activeView === "groups" && "chosen",
              )}
              onClick={() => setParticipantView("groups")}
              data-tour={tourAnchors ? "cc-add-groups" : undefined}
            >
              {isAdd
                ? "הוספת קבוצות"
                : formatTabCount("קבוצות במשימה", tabCounts?.groupLinks)}
            </button>
          )}
          <button
            type="button"
            className={clsx(
              "add-type-button",
              activeView === "individuals" && "chosen",
            )}
            onClick={() => setParticipantView("individuals")}
          >
            {isAdd
              ? isGroupOpportunity
                ? "הוספת מתנדבים בודדים"
                : "פרטניים"
              : formatTabCount(
                  isGroupOpportunity ? "מאושרים" : "פרטניים",
                  tabCounts?.approved,
                )}
          </button>
          <button
            type="button"
            className={clsx(
              "add-type-button",
              activeView === "pending" && "chosen",
            )}
            onClick={() => setParticipantView("pending")}
          >
            {formatTabCount("ממתינים לאישור", isAdd ? undefined : tabCounts?.pending)}
          </button>
        </Stack>
      {eventDetails?.dailyVolunteerTargetReached ? (
        <Typography color="warning.main" sx={{ mt: 2 }}>
          היעד היומי הושלם
        </Typography>
      ) : null}
      {isAdd && eventDetails?.dailyVolunteerTargetReached ? null : isAdd && !opportunityId ? null : activeView === "pending" ? (
        <OpportunityRequestsTable
          key={`pending-${opportunityId}`}
          id={opportunityId}
          refetchEvent={() => {
            void refetchEvent();
          }}
          onApprovedGroup={() => setParticipantView("groups")}
        />
      ) : isAdd ? (
        isGroupOpportunity ? (
          activeView === "groups" ? (
            <OptionalVolunteerGroupsTable
              key={`add-groups-${opportunityId}`}
              refetchEvent={refetchEvent}
              opportunity={eventDetails}
              tableId={`optional-volunteer-groups-add-${opportunityId}`}
              previewRows={previewGroups}
            />
          ) : (
            <OptionalParticipantsTable
              key={`add-individuals-${opportunityId}`}
              refetchEvent={refetchEvent}
              id={opportunityId}
              tableId={`optional-participants-add-individual-${opportunityId}`}
            />
          )
        ) : (
          <OptionalParticipantsTable
            key={`add-participants-${opportunityId}`}
            refetchEvent={refetchEvent}
            id={opportunityId}
            tableId={`optional-participants-add-${opportunityId}`}
          />
        )
      ) : activeView === "groups" ? (
        <ParticipantsGroupTable
          key={`view-groups-${opportunityId}`}
          refetchEvent={refetchEvent}
          opportunity={eventDetails}
        />
      ) : (
        <Stack flex={1} minHeight={0} gap={1} key={`view-individuals-${opportunityId}`}>
          {isGroupOpportunity && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              כל המתנדבים המאושרים במשימה, כולל מובילי קבוצה. להסרה: סמן והסר ממשימה.
            </Typography>
          )}
          <div className="participants-table-wrapper">
            <ParticipantsTable
              refetchEvent={refetchEvent}
              id={opportunityId}
              opportunity={eventDetails}
              tableId={
                isGroupOpportunity
                  ? `participants-approved-${opportunityId}`
                  : `participants-table-${opportunityId}`
              }
            />
          </div>
        </Stack>
      )}
      <Dialog
        open={!!iframeUrl}
        onClose={() => setIframeUrl("")}
      >
        <iframe src={iframeUrl} width="600" height="600" />
      </Dialog>
      <UserCardPopup
        userData={currentUserRowData}
        setUserData={setCurrentUserRowData}
      />
    </div>
  );
};
